import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import RowActionsMenu from '../../../components/dashboard/RowActionsMenu';
import SoftChip from '../../../components/dashboard/SoftChip';

const SCOPE_LABEL_KEY = {
  global: 'roles.scopes.global',
  business: 'roles.scopes.business',
  team: 'roles.scopes.team',
};

export const scopeLabel = (t, scope) => t(SCOPE_LABEL_KEY[scope] ?? scope);

// With `getRowHeight="auto"` (needed so the two-line "Perfil" cell doesn't
// clip in compact density), DataGrid stops vertically centering cell content
// by default — every other column's short value/chip renders pinned to the
// top of the row instead. Wrapping single-line cells in this restores the
// centering.
function CenteredCell({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      {children}
    </Box>
  );
}

export const buildRoleColumns = ({ t, onView, onEdit, onDelete }) => [
  {
    field: 'name',
    headerName: t('roles.fields.name'),
    flex: 1,
    minWidth: 200,
    resizable: false,
    renderCell: (params) => (
      <Stack spacing={0} sx={{ py: 1 }}>
        <Link component={RouterLink} to={`/roles/${params.row.uuid}`} variant="body2" underline="hover">
          {params.row.name}
        </Link>
        {params.row.description && (
          <Typography variant="caption" color="text.secondary">
            {params.row.description}
          </Typography>
        )}
      </Stack>
    ),
  },
  {
    field: 'scope',
    headerName: t('roles.fields.scope'),
    width: 140,
    valueGetter: (value, row) => scopeLabel(t, row.scope),
    renderCell: (params) => <CenteredCell>{params.value}</CenteredCell>,
  },
  {
    field: 'permissions',
    headerName: t('roles.fields.permissions'),
    width: 160,
    type: 'number',
    align: 'left',
    headerAlign: 'left',
    // Sorts/filters on the numeric count; the chip below is presentation-only.
    valueGetter: (value, row) => row.permissions?.length ?? 0,
    renderCell: (params) => (
      <CenteredCell>
        <Chip size="small" label={t('roles.list.permissionsCount', { count: params.value })} />
      </CenteredCell>
    ),
  },
  {
    field: 'collaborators_count',
    headerName: t('roles.fields.collaborators'),
    width: 140,
    renderCell: (params) => <CenteredCell>{params.value}</CenteredCell>,
  },
  {
    field: 'is_active',
    headerName: t('roles.fields.status'),
    width: 120,
    renderCell: (params) => (
      <CenteredCell>
        <SoftChip
          size="small"
          label={params.row.is_active ? t('roles.statuses.active') : t('roles.statuses.inactive')}
          color={params.row.is_active ? 'success' : 'default'}
        />
      </CenteredCell>
    ),
  },
  {
    field: 'rowActions',
    headerName: '',
    width: 56,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    align: 'center',
    renderCell: (params) => (
      <CenteredCell>
        <RowActionsMenu
          actions={[
            {
              label: t('roles.actions.view'),
              icon: <VisibilityOutlinedIcon fontSize="small" />,
              onClick: () => onView(params.row),
            },
            {
              label: t('roles.actions.edit'),
              icon: <EditOutlinedIcon fontSize="small" />,
              onClick: () => onEdit(params.row),
            },
            ...(params.row.is_owner
              ? []
              : [
                  {
                    label: t('roles.actions.delete'),
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    color: 'error',
                    onClick: () => onDelete(params.row),
                  },
                ]),
          ]}
        />
      </CenteredCell>
    ),
  },
];
